import { Button } from '@dbrain/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cellText, otherTimes, stripCells, stripTitle, type Schedule } from '@dbrain/app-core/microsoft365-write';

/**
 * The free/busy strip of the meeting card (MsToplantiOnay): "Üçünüzün takvimi, yarın öğleden sonra", the legend
 * (dolu hatched, seçilen dark, başka uygun green outline; a plain cell is everyone free but not offered), a cell per
 * interval with the hours under it, and "Başka saat seç": a click moves the meeting there and the chips follow, no
 * call. The cells are hatched and outlined, not told apart by colour alone, and each says itself in words.
 */
export function ScheduleStrip({
  schedule,
  start,
  end,
  original,
  people,
  timeZone,
  onPick,
}: {
  schedule: Schedule;
  start: Date;
  end: Date;
  /** the card's own start, offered again once another time is picked */
  original: Date;
  /** the organizer and the attendees */
  people: number;
  timeZone: string;
  /** "Başka saat seç"; absent once the card is decided */
  onPick?: (at: Date) => void;
}) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const alternatives = schedule.alternatives.map((iso) => new Date(iso)).filter((d) => !Number.isNaN(d.getTime()));
  const cells = stripCells(schedule, { start, end }, [original, ...alternatives], timeZone);
  const others = onPick ? otherTimes(schedule, original, start, timeZone) : [];
  if (cells.length === 0) return null;
  return (
    <div className="m365w-strip" data-testid="m365-write-strip">
      <div className="m365w-strip__head">
        <span id={titleId} className="m365w-strip__title" data-testid="m365-write-strip-title">
          {stripTitle(schedule, people, timeZone, new Date(), i18n.language, t)}
        </span>
        <span className="m365w-strip__legend" aria-hidden="true">
          <span className="m365w-legend m365w-legend--busy" />
          {t('microsoft365.chat.write.meeting.strip.legend.busy')}
          <span className="m365w-legend m365w-legend--selected" />
          {t('microsoft365.chat.write.meeting.strip.legend.selected')}
          <span className="m365w-legend m365w-legend--alternative" />
          {t('microsoft365.chat.write.meeting.strip.legend.alternative')}
        </span>
      </div>
      <ol className="m365w-strip__cells" aria-labelledby={titleId}>
        {cells.map((cell) => {
          const text = cellText(cell, t);
          return (
            <li key={cell.start.toISOString()} className={`m365w-cell m365w-cell--${cell.state}`} title={text} data-testid="m365-write-cell" data-state={cell.state} data-from={cell.from}>
              <span className="ui-visually-hidden">{text}</span>
            </li>
          );
        })}
      </ol>
      <div className="m365w-strip__hours" aria-hidden="true">
        {cells.map((cell) => (
          <span key={cell.start.toISOString()} className="m365w-strip__hour">
            {cell.hour ?? ''}
          </span>
        ))}
      </div>
      {others.length > 0 ? (
        <div className="m365w-strip__pick" data-testid="m365-write-pick">
          <span>{t('microsoft365.chat.write.actions.pick')}</span>
          {others.map((other) => (
            <Button
              key={other.at.toISOString()}
              size="sm"
              variant="secondary"
              aria-label={t('microsoft365.chat.write.actions.pickTime', { time: other.time })}
              onClick={() => onPick?.(other.at)}
              data-testid="m365-write-pick-time"
              data-time={other.time}
            >
              {other.time}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
